import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Row, Col, Form } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLinkedin, faYoutube, faMeta, faTiktok, faXTwitter } from "@fortawesome/free-brands-svg-icons";
import { faImage, faXmark } from "@fortawesome/free-solid-svg-icons";
import SelectField from "../../../Form/SelectField";
import NewModal from "../../../shared/Modals/NewModal";
import Filemanager from "../Files/Filemanager";
import crudApi from "@serie3/common/api/crudApi";
import DocumentsEndpoints from "@serie3/mymediaplanner/Modules/Documents/endpoints";
import YoutubeIcon from "../../../shared/YoutubeIcon";

// ── primitives ────────────────────────────────────────────────────────────────

export const inputStyle = {
  borderRadius: 8, border: "1.5px solid #e2e5ea", fontSize: 14,
  padding: "10px 14px", width: "100%", outline: "none", background: "#fff",
  color: "#111827", transition: "border-color 0.18s", fontFamily: "inherit",
};

export const Field = ({ label, children }) => (
  <div style={{ marginBottom: 18 }}>
    <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6, display: "block" }}>
      {label}
    </label>
    {children}
  </div>
);

export const Toggle = ({ label, checked, onChange }) => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0", borderBottom: "1px solid #f3f4f6" }}>
    <span style={{ fontSize: 13, color: "#374151" }}>{label}</span>
    <div
      onClick={() => onChange({ target: { checked: !checked } })}
      style={{
        width: 40, height: 22, borderRadius: 11, cursor: "pointer", flexShrink: 0,
        background: checked ? "#038edc" : "#d1d5db",
        position: "relative", transition: "background 0.2s ease",
      }}
    >
      <div style={{
        position: "absolute", top: 3, left: checked ? 21 : 3,
        width: 16, height: 16, borderRadius: "50%", background: "#fff",
        boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
        transition: "left 0.2s ease",
      }} />
    </div>
  </div>
);

export const SectionTitle = ({ children }) => (
  <p style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.8px", color: "#9299a8", marginBottom: 10, marginTop: 24 }}>
    {children}
  </p>
);

export const ImageSelector = ({ label, fileId, onChange, onSetForAll }) => {
  const [showModal, setShowModal] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const prevFileId = useRef(null);
  const revokeUrl = useRef(null);

  useEffect(() => {
    if (!fileId) {
      setPreviewUrl(null);
      return;
    }
    if (fileId === prevFileId.current) return;
    prevFileId.current = fileId;
    setPreviewUrl(null);

    let cancelled = false;
    const doFetch = async () => {
      try {
        const res = await crudApi.getBinary(DocumentsEndpoints.show + fileId);
        if (cancelled) return;
        const blob = new Blob([res.data], { type: res.headers["content-type"] });
        const url = URL.createObjectURL(blob);
        if (revokeUrl.current) URL.revokeObjectURL(revokeUrl.current);
        revokeUrl.current = url;
        setPreviewUrl(url);
      } catch {
        // leave previewUrl null — placeholder still shows
      }
    };
    doFetch();
    return () => { cancelled = true; };
  }, [fileId]);

  const handleFileSelected = (file) => {
    onChange(file.id);
    setShowModal(false);
  };

  const handleClear = () => {
    onChange(null);
    setPreviewUrl(null);
  };

  const hasFile = !!fileId;

  return (
    <div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      {label && (
        <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 8, display: "block" }}>
          {label}
        </label>
      )}
      {/* preview area */}
      <div
        onClick={() => setShowModal(true)}
        style={{
          width: "100%", aspectRatio: "16/9", borderRadius: 10, cursor: "pointer",
          overflow: "hidden", position: "relative",
          border: hasFile ? "2px solid #038edc" : "2px dashed #d1d5db",
          background: previewUrl ? "#000" : "#f0f2f5",
          display: "flex", alignItems: "center", justifyContent: "center",
          transition: "border-color 0.15s",
        }}
        onMouseEnter={(e) => { if (!hasFile) e.currentTarget.style.borderColor = "#038edc"; }}
        onMouseLeave={(e) => { if (!hasFile) e.currentTarget.style.borderColor = "#d1d5db"; }}
      >
        {previewUrl ? (
          <img src={previewUrl} alt="preview" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : hasFile ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: "50%", border: "3px solid #038edc", borderTopColor: "transparent", animation: "spin 0.8s linear infinite" }} />
            <span style={{ fontSize: 12, color: "#9ca3af" }}>Loading…</span>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
            <FontAwesomeIcon icon={faImage} style={{ fontSize: 32, color: "#9ca3af" }} />
            <span style={{ fontSize: 13, color: "#6b7280", fontWeight: 500 }}>Click to select image</span>
          </div>
        )}
        {/* hover overlay when file present */}
        {hasFile && (
          <div style={{
            position: "absolute", inset: 0, background: "rgba(0,0,0,0.45)",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
            opacity: 0, transition: "opacity 0.15s",
          }}
            onMouseEnter={(e) => { e.currentTarget.style.opacity = 1; }}
            onMouseLeave={(e) => { e.currentTarget.style.opacity = 0; }}
          >
            <span style={{ fontSize: 12, color: "#fff", fontWeight: 600, background: "rgba(255,255,255,0.15)", padding: "6px 14px", borderRadius: 6 }}>Change</span>
          </div>
        )}
      </div>
      {/* action buttons */}
      {hasFile && (
        <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
          {onSetForAll && (
            <button
              type="button"
              onClick={() => onSetForAll(fileId)}
              style={{
                flex: 1, padding: "6px 0", borderRadius: 7,
                border: "1.5px solid #d1fae5", background: "#fff", color: "#059669",
                fontSize: 12, fontWeight: 600, cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              }}
            >
              Set for all
            </button>
          )}
          <button
            type="button"
            onClick={handleClear}
            style={{
              flex: 1, padding: "6px 0", borderRadius: 7,
              border: "1.5px solid #fde8e8", background: "#fff", color: "#f34e4e",
              fontSize: 12, fontWeight: 600, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}
          >
            <FontAwesomeIcon icon={faXmark} style={{ fontSize: 11 }} />
            Remove
          </button>
        </div>
      )}
      <NewModal show={showModal} setShow={setShowModal} title={label ?? "Select Image"} size="xl">
        <Filemanager handleFileFunction={handleFileSelected} useGrid={true} showAdd={true} />
      </NewModal>
    </div>
  );
};

export const TagInput = ({ value, onChange }) => {
  const [input, setInput] = useState("");
  const tags = value ?? [];
  const add = () => {
    const trimmed = input.trim();
    if (trimmed && !tags.includes(trimmed)) onChange([...tags, trimmed]);
    setInput("");
  };
  const remove = (tag) => onChange(tags.filter((t) => t !== tag));
  return (
    <div>
      <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
        <input value={input} onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder="Add tag and press Enter" style={{ ...inputStyle, flex: 1 }} />
        <button type="button" onClick={add}
          style={{ padding: "0 16px", borderRadius: 8, border: "none", background: "#038edc", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
          Add
        </button>
      </div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {tags.map((tag) => (
          <span key={tag} style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 10px", borderRadius: 20, background: "rgba(3,142,220,0.1)", color: "#038edc", fontSize: 12, fontWeight: 600 }}>
            {tag}
            <span onClick={() => remove(tag)} style={{ cursor: "pointer", opacity: 0.7, fontSize: 14, lineHeight: 1 }}>×</span>
          </span>
        ))}
      </div>
    </div>
  );
};

// ── platform forms ────────────────────────────────────────────────────────────

export const YoutubeForm = ({ formik }) => {
  const { t } = useTranslation();
  const p = "youtube";
  const v = formik.values.youtube;
  const set = (path, val) => formik.setFieldValue(`${p}.${path}`, val);
  return (
    <>
      <Field label={t("Backend.YouTube.Title", "Title")}>
        <input
          style={inputStyle}
          value={v.title ?? ""}
          onChange={(e) => set("title", e.target.value)}
          maxLength={100}
          placeholder={t("Backend.YouTube.TitlePlaceholder", "Video title (max 100 chars)")}
        />
      </Field>
      <Field label={t("Backend.YouTube.Description", "Description")}>
        <textarea
          style={{ ...inputStyle, minHeight: 100, resize: "vertical" }}
          value={v.description ?? ""}
          onChange={(e) => set("description", e.target.value)}
          maxLength={5000}
          placeholder={t("Backend.YouTube.DescriptionPlaceholder", "Video description (max 5000 chars)")}
        />
      </Field>
      <SelectField label="Visibility" value={v.visibility} onChange={(e) => set("visibility", e.target.value)}>
        <option value="private">Private</option>
        <option value="unlisted">Unlisted</option>
        <option value="public">Public</option>
      </SelectField>
      <SectionTitle>Audience</SectionTitle>
      <Toggle label="Made for Kids" checked={v.audience.madeForKids} onChange={(e) => set("audience.madeForKids", e.target.checked)} />
      <SectionTitle>Metadata</SectionTitle>
      <Field label="Tags">
        <TagInput value={v.metadata.tags} onChange={(tags) => set("metadata.tags", tags)} />
      </Field>
      <Row>
        <Col md={4}>
          <Field label={t("Backend.YouTube.Category", "Category")}>
            <select
              style={inputStyle}
              value={v.metadata.categoryId ?? ""}
              onChange={(e) => set("metadata.categoryId", e.target.value || null)}
            >
              <option value="">{t("Backend.YouTube.Categories.None", "— Select category —")}</option>
              <option value="1">{t("Backend.YouTube.Categories.FilmAnimation", "Film & Animation")}</option>
              <option value="2">{t("Backend.YouTube.Categories.AutosVehicles", "Autos & Vehicles")}</option>
              <option value="10">{t("Backend.YouTube.Categories.Music", "Music")}</option>
              <option value="15">{t("Backend.YouTube.Categories.PetsAnimals", "Pets & Animals")}</option>
              <option value="17">{t("Backend.YouTube.Categories.Sports", "Sports")}</option>
              <option value="19">{t("Backend.YouTube.Categories.TravelEvents", "Travel & Events")}</option>
              <option value="20">{t("Backend.YouTube.Categories.Gaming", "Gaming")}</option>
              <option value="22">{t("Backend.YouTube.Categories.PeopleBlogs", "People & Blogs")}</option>
              <option value="23">{t("Backend.YouTube.Categories.Comedy", "Comedy")}</option>
              <option value="24">{t("Backend.YouTube.Categories.Entertainment", "Entertainment")}</option>
              <option value="25">{t("Backend.YouTube.Categories.NewsPolitics", "News & Politics")}</option>
              <option value="26">{t("Backend.YouTube.Categories.HowtoStyle", "Howto & Style")}</option>
              <option value="27">{t("Backend.YouTube.Categories.Education", "Education")}</option>
              <option value="28">{t("Backend.YouTube.Categories.ScienceTechnology", "Science & Technology")}</option>
              <option value="29">{t("Backend.YouTube.Categories.NonprofitsActivism", "Nonprofits & Activism")}</option>
            </select>
          </Field>
        </Col>
        <Col md={4}>
          <Field label={t("Backend.YouTube.DefaultLanguage", "Default Language")}>
            <input style={inputStyle} value={v.metadata.defaultLanguage ?? ""} onChange={(e) => set("metadata.defaultLanguage", e.target.value || null)} placeholder="e.g. en" />
          </Field>
        </Col>
        <Col md={4}>
          <Field label={t("Backend.YouTube.AudioLanguage", "Audio Language")}>
            <input style={inputStyle} value={v.metadata.audioLanguage ?? ""} onChange={(e) => set("metadata.audioLanguage", e.target.value || null)} placeholder="e.g. en" />
          </Field>
        </Col>
      </Row>
      <SectionTitle>Thumbnail</SectionTitle>
      <SelectField label="Thumbnail Mode" value={v.thumbnail.mode} onChange={(e) => set("thumbnail.mode", e.target.value)}>
        <option value="auto">Auto</option>
        <option value="custom">Custom</option>
      </SelectField>
      <SectionTitle>Distribution</SectionTitle>
      <div style={{ marginTop: 12 }}>
        <SelectField label="License" value={v.distribution.license} onChange={(e) => set("distribution.license", e.target.value)}>
          <option value="youtube">Standard YouTube License</option>
          <option value="creativeCommon">Creative Commons</option>
        </SelectField>
      </div>
    </>
  );
};

export const MetaForm = ({ formik }) => {
  const p = "meta";
  const v = formik.values.meta;
  const set = (path, val) => formik.setFieldValue(`${p}.${path}`, val);
  return (
    <>
      <Row>
        <Col md={6}>
          <SelectField label="Surface" value={v.surface} onChange={(e) => set("surface", e.target.value)}>
            <option value="reel">Reel</option>
            <option value="post">Post</option>
            <option value="story">Story</option>
          </SelectField>
        </Col>
        <Col md={6}>
          <SelectField label="Publish State" value={v.publishState} onChange={(e) => set("publishState", e.target.value)}>
            <option value="draft">Draft</option>
            <option value="publish_now">Publish Now</option>
          </SelectField>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <SelectField label="Visibility" value={v.visibility.value} onChange={(e) => set("visibility.value", e.target.value)}>
            <option value="public">Public</option>
            <option value="friends">Friends</option>
            <option value="private">Only Me</option>
          </SelectField>
        </Col>
      </Row>
      <Field label="Caption">
        <textarea style={{ ...inputStyle, minHeight: 80, resize: "vertical" }} value={v.caption} onChange={(e) => set("caption", e.target.value)} placeholder="Caption text" />
      </Field>
      <Field label="Hashtags">
        <TagInput value={v.hashtags} onChange={(tags) => set("hashtags", tags)} />
      </Field>
      <SectionTitle>Cover</SectionTitle>
      <SelectField label="Cover Mode" value={v.cover.mode} onChange={(e) => set("cover.mode", e.target.value)}>
        <option value="frame">Frame</option>
        <option value="custom">Custom</option>
      </SelectField>
      <SectionTitle>Distribution</SectionTitle>
      <Toggle label="Share to Feed" checked={v.distribution.shareToFeed} onChange={(e) => set("distribution.shareToFeed", e.target.checked)} />
      <SectionTitle>Engagement</SectionTitle>
      <Toggle label="Comments Enabled" checked={v.engagement.commentsEnabled} onChange={(e) => set("engagement.commentsEnabled", e.target.checked)} />
      <SectionTitle>Branded Content</SectionTitle>
      <Toggle label="Branded Content Enabled" checked={v.brandedContent.enabled} onChange={(e) => set("brandedContent.enabled", e.target.checked)} />
    </>
  );
};

export const TikTokForm = ({ formik }) => {
  const p = "tiktok";
  const v = formik.values.tiktok;
  const set = (path, val) => formik.setFieldValue(`${p}.${path}`, val);
  return (
    <>
      <Row>
        <Col md={6}>
          <SelectField label="Visibility" value={v.privacy.visibility} onChange={(e) => set("privacy.visibility", e.target.value)}>
            <option value="public">Public</option>
            <option value="friends">Friends</option>
            <option value="private">Private</option>
          </SelectField>
        </Col>
        <Col md={6}>
          <SelectField label="Publish State" value={v.publishState} onChange={(e) => set("publishState", e.target.value)}>
            <option value="draft">Draft</option>
            <option value="publish_now">Publish Now</option>
          </SelectField>
        </Col>
      </Row>
      <Field label="Hashtags">
        <TagInput value={v.hashtags} onChange={(tags) => set("hashtags", tags)} />
      </Field>
      <SectionTitle>Engagement</SectionTitle>
      <Toggle label="Comments Enabled" checked={v.engagement.commentsEnabled} onChange={(e) => set("engagement.commentsEnabled", e.target.checked)} />
      <SectionTitle>Reuse</SectionTitle>
      <Toggle label="Allow Duet" checked={v.reuse.allowDuet} onChange={(e) => set("reuse.allowDuet", e.target.checked)} />
      <Toggle label="Allow Stitch" checked={v.reuse.allowStitch} onChange={(e) => set("reuse.allowStitch", e.target.checked)} />
      <SectionTitle>Branded Content</SectionTitle>
      <Toggle label="Branded Content Enabled" checked={v.brandedContent.enabled} onChange={(e) => set("brandedContent.enabled", e.target.checked)} />
    </>
  );
};

export const LinkedInForm = ({ formik }) => {
  const p = "linkedin";
  const v = formik.values.linkedin;
  const set = (path, val) => formik.setFieldValue(`${p}.${path}`, val);
  return (
    <>
      <Row>
        <Col md={6}>
          <SelectField label="Visibility" value={v.visibility} onChange={(e) => set("visibility", e.target.value)}>
            <option value="anyone">Anyone</option>
            <option value="connections">Connections Only</option>
          </SelectField>
        </Col>
        <Col md={6}>
          <SelectField label="Publish State" value={v.publishState} onChange={(e) => set("publishState", e.target.value)}>
            <option value="publish_now">Publish Now</option>
            <option value="draft">Draft</option>
          </SelectField>
        </Col>
      </Row>
      <Field label="Post Text">
        <textarea style={{ ...inputStyle, minHeight: 80, resize: "vertical" }} value={v.postText} onChange={(e) => set("postText", e.target.value)} placeholder="Post text" />
      </Field>
      <SectionTitle>Video</SectionTitle>
      <SelectField label="Thumbnail Mode" value={v.video.thumbnailMode} onChange={(e) => set("video.thumbnailMode", e.target.value)}>
        <option value="auto">Auto</option>
        <option value="custom">Custom</option>
      </SelectField>
    </>
  );
};


export const XForm = ({ formik }) => {
  const p = "x";
  const v = formik.values.x;
  const set = (path, val) => formik.setFieldValue(`${p}.${path}`, val);
  return (
    <>
      <Field label="Text">
        <textarea style={{ ...inputStyle, minHeight: 80, resize: "vertical" }} value={v.text} onChange={(e) => set("text", e.target.value)} placeholder="Post text" />
      </Field>
      <Field label="Hashtags">
        <TagInput value={v.hashtags} onChange={(tags) => set("hashtags", tags)} />
      </Field>
      <SectionTitle>Engagement</SectionTitle>
      <SelectField label="Reply Settings" value={v.engagement.replySettings} onChange={(e) => set("engagement.replySettings", e.target.value)}>
        <option value="everyone">Everyone</option>
        <option value="followers">Followers</option>
        <option value="mentioned">Mentioned Only</option>
      </SelectField>
      <SectionTitle>Content</SectionTitle>
      <Toggle label="Flag Sensitive Content" checked={v.sensitiveContent.flagged} onChange={(e) => set("sensitiveContent.flagged", e.target.checked)} />
    </>
  );
};

// ── platform list ─────────────────────────────────────────────────────────────

export const PLATFORMS = [
  { key: "youtube",  label: "YouTube",  icon: faYoutube,  color: "#ff0000", Form: YoutubeForm,
    imageField: { label: "Thumbnail", getFileId: (v) => v.thumbnail?.fileId, setPath: "thumbnail.fileId" } },
  { key: "meta",     label: "Meta",     icon: faMeta,     color: "#0081fb", Form: MetaForm,
    imageField: { label: "Cover Image", getFileId: (v) => v.cover?.fileId, setPath: "cover.fileId" } },
  { key: "tiktok",   label: "TikTok",   icon: faTiktok,   color: "#010101", Form: TikTokForm,
    imageField: { label: "Cover Image", getFileId: (v) => v.cover?.fileId, setPath: "cover.fileId" } },
  { key: "linkedin", label: "LinkedIn", icon: faLinkedin, color: "#0077b5", Form: LinkedInForm,
    imageField: { label: "Video Thumbnail", getFileId: (v) => v.video?.thumbnailFileId, setPath: "video.thumbnailFileId" } },
  { key: "x",        label: "X",        icon: faXTwitter, color: "#000000", Form: XForm },
];

// ── shared sidebar + form layout ──────────────────────────────────────────────

export const PlatformFormLayout = ({ formik, activeKey, setActiveKey, onSubmit }) => {
  const active = PLATFORMS.find((p) => p.key === activeKey);
  return (
    <Form onSubmit={onSubmit}>
      <div className="d-md-flex" style={{ gap: 20 }}>
        {/* sidebar */}
        <div style={{ minWidth: 200, maxWidth: 220, flexShrink: 0 }}>
          <div style={{ background: "#fff", border: "1px solid #eaecf0", borderRadius: 12, padding: "16px 12px", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
            <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: "#9299a8", marginBottom: 8, paddingLeft: 8 }}>
              Platforms
            </p>
            <ul className="list-unstyled" style={{ margin: 0, display: "flex", flexDirection: "column", gap: 2 }}>
              {PLATFORMS.map(({ key, label, icon, color }) => {
                const isActive = key === activeKey;
                return (
                  <li key={key}>
                    <button
                      type="button"
                      onClick={() => setActiveKey(key)}
                      style={{
                        display: "flex", alignItems: "center", gap: 10,
                        width: "100%", padding: "9px 10px", borderRadius: 8,
                        border: "none", cursor: "pointer", fontFamily: "inherit",
                        fontSize: 13, fontWeight: 600,
                        background: isActive ? `${color}15` : "transparent",
                        color: isActive ? color : "#495057",
                        transition: "background 0.15s ease, color 0.15s ease",
                      }}
                      onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = "#f8f9fa"; }}
                      onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = "transparent"; }}
                    >
                      {key === "youtube"
                        ? <YoutubeIcon height={20} />
                        : <FontAwesomeIcon icon={icon} style={{ fontSize: 15, width: 16 }} />}
                      {label}
                    </button>
                  </li>
                );
              })}
            </ul>
            <button
              type="submit"
              style={{
                marginTop: 12, width: "100%", padding: "9px 0", borderRadius: 8,
                border: "none", cursor: "pointer", fontFamily: "inherit",
                fontSize: 13, fontWeight: 600, background: "#038edc", color: "#fff",
              }}
            >
              Save Settings
            </button>
          </div>
        </div>

        {/* form panel */}
        <div className="w-100">
          <div style={{ background: "#fff", border: "1px solid #eaecf0", borderRadius: 12, padding: 24 }}>
            {active && (
              <>
                {/* header */}
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20, paddingBottom: 16, borderBottom: "1px solid #f3f4f6" }}>
                  {active.key === "youtube"
                    ? <YoutubeIcon height={24} />
                    : <FontAwesomeIcon icon={active.icon} style={{ fontSize: 20, color: active.color }} />}
                  <h6 style={{ margin: 0, fontWeight: 700, color: "#1a2236", fontSize: 15 }}>
                    {active.label}
                  </h6>
                </div>
                {/* body: form left, image selector right */}
                <div style={{ display: "flex", gap: 32, alignItems: "flex-start" }}>
                  <div style={{ width: "50%", minWidth: 0 }}>
                    <active.Form formik={formik} />
                  </div>
                  {active.imageField && (
                    <div style={{ width: "50%", flexShrink: 0 }}>
                      <ImageSelector
                        label={active.imageField.label}
                        fileId={active.imageField.getFileId(formik.values[active.key])}
                        onChange={(id) => formik.setFieldValue(`${active.key}.${active.imageField.setPath}`, id)}
                        onSetForAll={(id) => {
                          PLATFORMS.forEach((p) => {
                            if (p.imageField) {
                              formik.setFieldValue(`${p.key}.${p.imageField.setPath}`, id);
                            }
                          });
                        }}
                      />
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </Form>
  );
};
